import { DialogComponent } from "@theme/dialog";
import { ThemeEvents } from "@theme/events";

/**
 * Site-wide drawer around DialogComponent.
 * Moves `[data-drawer-view]` into the dialog on open and back out on close.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */

class DrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    // Open triggers for drawers without an id.
    document.addEventListener("click", this.#handleDocumentClick);

    // Close from elements inside the drawer content.
    this.addEventListener("click", this.#handleInternalCloseClick);

    // Any close path: cross, outside click, Escape, or close().
    this.refs.dialog?.addEventListener("close", this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("click", this.#handleDocumentClick);
    this.removeEventListener("click", this.#handleInternalCloseClick);
    this.refs.dialog?.removeEventListener("close", this.#handleDialogClose);
  }

  #handleDocumentClick = (event) => {
    const btn = event.target.closest(`[data-drawer-open="drawer"]`);
    if (!btn) return;

    // Named drawers (with id) use their own scoped triggers.
    if (this.id) return;

    this.openFromTrigger(btn);
  };

  #handleInternalCloseClick = (event) => {
    const closeEl = event.target.closest("[data-drawer-close]");
    if (!closeEl) return;

    event.preventDefault();
    this.close();
  };

  #handleDialogClose = () => {
    const mount = this.refs.dialog?.querySelector("[data-drawer-content]");
    const content = mount?.firstElementChild;

    // Announced while the view is still mounted, so listeners inside it are attached.
    if (content instanceof HTMLElement && content.dataset.drawerView) {
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.drawerClose, {
          detail: { view: content.dataset.drawerView, content }
        })
      );
    }

    this.#restoreDrawerContent(mount);
  };

  openFromTrigger(trigger) {
    const { dialog } = this.refs;
    if (!dialog) return;

    const view = trigger?.getAttribute("data-drawer-view");
    const title = trigger?.getAttribute("data-drawer-title");

    if (title) {
      const titleEl = dialog.querySelector("[data-drawer-title]");
      if (titleEl) titleEl.innerHTML = title;
    }

    const tabIndex = trigger?.getAttribute("data-tab-index");
    if (tabIndex != null && view) {
      const source = document.querySelector(`[data-drawer-view="${CSS.escape(view)}"]:not([data-drawer-open])`);
      source?.setAttribute("data-active-tab", tabIndex);
    }

    if (view) {
      this.#renderView(dialog, view);
    }

    this.showDialog();
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  /** Hide the view and put it back on `document.body` for the next open. */
  #restoreDrawerContent(mount) {
    const content = mount?.firstElementChild;
    if (!content?.dataset.drawerView) return;

    content.hidden = true;
    document.body.appendChild(content);
  }

  /** Move `[data-drawer-view="…"]` from the page into the dialog. */
  #renderView(dialog, view) {
    const mount = dialog.querySelector("[data-drawer-content]");
    if (!mount) return;

    const source = document.querySelector(`[data-drawer-view="${CSS.escape(view)}"]:not([data-drawer-open])`);
    if (!source) {
      mount.innerHTML = `<div class="py-6 text-sm">Missing drawer view: ${view}</div>`;
      return;
    }

    source.hidden = false;
    mount.replaceChildren(source);
  }
}

if (!customElements.get("drawer-component")) {
  customElements.define("drawer-component", DrawerComponent);
}
